import type zhCN from './zh-CN'

/**
 * 英文文案。类型声明为 `typeof zhCN`，所以**少一个键、多一个键都是编译错误**——
 * `vue-tsc --noEmit` 通过即等于中英文案键完全对齐，不需要额外的对齐脚本。
 *
 * 澳洲英文口径：Client Portal、trading account、withdrawal（不是 withdraw money）、
 * BSB、AUD/USD。需求 §3 的四条口径同样适用于英文：
 * 不得出现 regulatory requirement / free / no fee 之类表述。
 */
const messages: typeof zhCN = {
  app: {
    title: 'ZR Markets Client Portal',
    brand: 'ZR Markets',
    portal: 'Client Portal',
  },

  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    later: 'Later',
    close: 'Close',
    submit: 'Submit',
    save: 'Save',
    edit: 'Change',
    back: 'Back',
    next: 'Next',
    prev: 'Previous',
    retry: 'Try again',
    refresh: 'Refresh',
    copy: 'Copy',
    copied: 'Copied ✓',
    copyFailed: 'Copy failed, please copy manually',
    today: 'Today',
    clear: 'Clear',
    pickYearMonth: 'Pick year and month',
    loading: 'Loading…',
    empty: 'Nothing to show',
    required: 'Required',
    optional: 'Optional',
    all: 'All',
    status: 'Status',
    amount: 'Amount',
    currency: 'Currency',
    account: 'Trading account',
    manage: 'Manage',
    contactSupport: 'Contact us',
    emailSent: 'Email sent',
    loadFailed: 'Could not load. Please try again.',
    networkError: 'Network problem. Please try again.',
    notAvailableYet: 'The backend service for this feature is not available yet.',
  },

  nav: {
    companyInfo: 'Company information',
    companyPeople: 'People and documents',
    overview: 'Overview',
    overviewBeforeOnboarding: 'Open Account',
    accounts: 'Accounts',
    myAccount: 'My Account',
    profile: 'My Profile',
    funding: 'Funding',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    bankAccounts: 'Bank Accounts',
    reports: 'Reports',
    depositRecords: 'Deposit Records',
    withdrawalRecords: 'Withdrawal Records',
    platform: 'Platform Download',
    support: 'Help & Support',
    logout: 'Log out',
    menu: 'Menu',
    notReady: 'Not available yet',
  },

  auth: {
    // Left-hand brand pane of the login page (same copy as the onboarding sign-up screens)
    brand: {
      title: 'Regulated by ASIC in Australia',
      licence: 'AFSL 562700',
      segregated: 'Client money held in segregated accounts.',
      f0: 'ASIC regulated AFSL 562700',
      f1: 'MetaTrader 5 platform',
      f2: 'Demo account on sign-up',
    },
    loginTitle: 'Log in to your Client Portal',
    email: 'Email address',
    password: 'Password',
    remember: 'Keep me logged in on this device',
    forgot: 'Forgot your password?',
    login: 'Log in',
    noAccount: "Don't have an account?",
    openAccount: 'Open an account',
    badCredentials: 'Incorrect email or password',
    emailInvalid: 'Enter a valid email address',
    passwordRequired: 'Enter your password',
    loggingIn: 'Logging in…',

    forgotTitle: 'Reset your password',
    forgotDesc: 'Enter your registered email address and we will send you a reset link.',
    sendLink: 'Send reset link',
    forgotSent: 'If that email is registered, a reset link has been sent.',
    forgotSentHint: 'The link is valid for 30 minutes. Please check your inbox and junk folder.',
    backToLogin: 'Back to log in',

    resetTitle: 'Set a new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    mismatch: 'The two passwords do not match',
    resetDone: 'Your new password is active',
    resetDoneDesc: 'A confirmation email has been sent, and all other devices have been logged out.',
    linkExpired: 'This link has expired',
    linkExpiredDesc: 'Reset links are valid for 30 minutes only. Please request a new one.',
    requestAgain: 'Request a new link',

    changeTitle: 'Change your password',
    currentPassword: 'Current password',
    changeDone: 'Password updated. A confirmation email has been sent, and all other devices have been logged out.',

    ruleTitle: 'Your password must have:',
    ruleLength: '8–20 characters',
    ruleUpper: 'at least 1 uppercase letter',
    ruleLower: 'at least 1 lowercase letter',
    ruleDigit: 'at least 1 number',
    ruleNotSame: 'a value different from your current password',

    lockedTitle: 'Account temporarily locked',

    // ---- Login admission denied (API 0.10.0 / V1.4 §5.4): three reasons, three destinations ----
    reviewPendingTitle: 'Your application is under review',
    reviewPendingDesc: 'We have received your account application and it is currently under review. You cannot sign in to the Client Portal while the review is in progress — this is not an error on your side, and there is no need to try again.',
    reviewPendingNext: 'We will email you as soon as the review is complete, and you will be able to sign in then.',
    reviewPendingContact: 'To ask about progress, contact Client Services:',
    denyApplicationDeclined: 'Unfortunately your account application was not approved, so you cannot sign in to the Client Portal. For details, please contact Client Services at {email}.',
    denyPortalDisabled: 'This account is currently unavailable. Please contact Client Services.',
    clientTypeMissing: 'Sign-in information is incomplete. Please try again shortly, and contact Client Services if it keeps happening.',

    // ---- First sign-in forced password change (V1.4 §5.2): same page as "password expired", different copy ----
    firstLoginTitle: 'First sign-in — please set your password',
    firstLoginDesc: 'You are currently using the initial password from your welcome email. For your security, please set a new password that only you know.',
    // Shown as a note under the checklist: the first-login page has no current-password field
    ruleNotSameInitial: 'Your new password must differ from the initial password.',
  },

  /** Corporate related-person KYC list (V1.4 §6A) */
  kycPeople: {
    reverifyRequested: 'Verify with new ID',
    reverifySubmitted: 'New ID under review',
    reverifyPassed: 'New ID verified · Update pending',
    reverifyRetry: 'New ID not verified · Retake photos',
    reverifyRejected: 'New ID not verified · Contact Client Services',
    titlePre: 'Identity verification for related persons',
    titleOpened: 'People and documents',
    passedOf: '{passed} of {total} verified',
    selfTag: 'You',
    noContact: 'No contact details on record',
    /* Same wording for yourself and for everyone else, per the prototype (V1.4 §6A #5) */
    verifyHere: 'Verify on this device',
    reverifyHere: 'Re-verify on this device',
    useMobile: 'Scan with phone',
    sendLink: 'Send verification link',
    resendLink: 'Resend link',
    sendReverifyLink: 'Send re-verification link',
    inviteSent: 'Verification link sent to {masked}, valid for 7 days. This page updates automatically once {name} is done.',
    inviteSelfHint: 'This is you — use "Verify on this device" instead.',
    invitePassedHint: 'This person has already been verified.',
    inviteNoContact: 'No contact details on record, so no link can be sent. Please contact Client Services.',
    resubmitSaved:
      'Everything else {name} submitted has been saved — only the step that was not accepted needs to be redone, using any of the options above.',
    resubmitNotifiedBoth: 'The reason and instructions have been emailed to you and to this person.',
    resubmitNotifiedRep: 'The reason and instructions have been emailed to you.',
    reviewNote: 'The documents have been submitted and are being reviewed by our team. Nothing needs to be resubmitted — we will email you the outcome.',
    failedNote: 'This person’s verification was not accepted. It has to be reopened by us before they can try again. Please',
    startedAt: 'Verification started {time} (UTC)',
    syncing: 'Checking for updates…',
    notCollectedNote: 'We do not have this person’s document expiry date on file. It does not affect their verification — to have it added,',
    docsToggle: 'Verification and documents',
    docId: 'Identity document',
    docPoa: 'Proof of address',
    docAml: 'AML screening',
    validUntil: 'Valid until {date}',
    nextUpdate: 'Next update {date}',
    /* Phase 2 §14.7: proof of address is its own Sumsub step */
    poaVerified: 'Verified',
    amlPassed: 'Passed',
    daysLeft: '{n} days left — please re-verify before then',
    expiredNow: 'Please re-verify now',
    docsNote:
      'Under our account policy, identity documents must be re-verified before they expire and proof of address is refreshed periodically; we will remind you in advance. The identity document and proof of address are captured in the same session. AML screening is automated — nothing else needs to be submitted.',
    allPassed: 'Everyone has been verified. We are opening the company MT5 trading account and will email you once it is ready.',
    someFailed: 'One or more verifications were not successful, so the account cannot be opened yet. For details,',
    empty: 'No related persons on record. Please contact Client Services if this looks wrong.',
    footPre:
      'Only the authorised representative can log in to the Client Portal; company persons do not get a login. Each person must complete their own document capture and liveness check — on this device, by scanning the QR code with their own phone, or via a verification link sent to their email (valid for 7 days, and it can be sent again after that). Each person only needs to verify once.',
    footOpened: 'We email a reminder 60 days before a document expires. A re-verify action appears here when a document is expiring or has expired.',
  },

  /** Pre-verification confirmation card (V1.4 §6A) */
  kycConfirm: {
    title: 'Before you start, please check these details',
    desc: 'These are the details we hold for you. Please confirm they match the document you are about to upload.',
    reverifyDescPerson: 'We are updating the identity details of {name} ({role}) as your company requested. Please complete this verification with the new ID (not the old one); once it passes and is reviewed, the record is updated.',
    descPerson: 'These are the details we hold for {name} ({role}). Please confirm they match the document about to be uploaded.',
    name: 'Name',
    dob: 'Date of birth',
    address: 'Residential address',
    idType: 'Document type',
    idNumber: 'Document number',
    idExpiry: 'Expiry date',
    idCountry: 'Issuing country',
    warn: 'If these do not match the document, the verification goes to manual review and takes longer. Please contact Client Services to correct them before starting.',
    proceed: 'Details are correct, continue',
  },

  /** Retail pre-check page (V1.4 §6A) */
  kycStart: {
    title: 'Start identity verification',
    titleReverify: 'Re-verify your identity',
    stepN: 'Step {n}',
    step1: 'ID document: passport or driver licence, photograph both sides as prompted',
    step2: 'Liveness check: a short selfie video, completed by you in person',
    step3: 'Proof of address: a bank statement or utility bill from the last 3 months',
    recordTitle: 'The details we hold',
    name: 'Name',
    dob: 'Date of birth',
    address: 'Residential address',
    country: 'Country of residence',
    warn: 'Please confirm these match the document you are about to upload. A mismatch sends the verification to manual review and takes longer.',
    proceed: 'Details are correct, start',
    mismatchHint: 'Something wrong? Contact Client Service Team to correct it first — details cannot be changed during verification.',
    useNewId: 'Please use your new ID',
    proceedNewId: 'Start with my new ID',
    useNewIdDesc: 'We are updating your identity details as requested. Please complete this verification with your new ID (not the old one); once it passes and is reviewed, your account is updated.',
    rekycTitle: 'Please resubmit your verification documents',
    rekycDesc: 'Client Services has asked you to complete identity verification again (for example, proof of identity or proof of address). Please check the details we hold below, then follow the prompts to capture and upload your documents. We will review them as soon as they are submitted.',
    /* Submitted and awaiting manual review: no "start" here, and the server declines to issue an SDK token */
    underReview: 'Your details have been submitted and we are reviewing them (usually within one business day), so there is nothing to submit again. We will email you the result.',
  },

  /** SDK host panel */
  kycSdk: {
    verifying: 'Verifying: {name}',
    selfTag: 'you',
    backToList: 'Back to list',
    byPersonHint: 'This person must complete the document capture and liveness check in person.',
    byYouHint: 'Please complete the document capture and liveness check in person.',
    /* stepLine / step1 / step2 / countryHint removed — the SDK shows its own steps (see zh-CN) */
    sdkLoadFailed: 'The verification component could not be loaded. Check your connection and try again; if it keeps failing, your network may be blocking the external verification service — please contact support.',
    sdkBroken: 'The verification component could not start. Please contact support.',
  },

  /** Continue-on-phone QR code */
  kycQr: {
    title: 'Scan with phone',
    desc: 'Ask {name} to scan the QR code below and follow the prompts.',
    scanHint: 'Scan with the phone camera — no account registration needed.',
    expire: 'This QR code is personal to this person, can be scanned more than once and is valid for 7 days.',
    linkLabel: 'Or copy the link and send it to them',
    refresh: 'New link',
    review: 'Submitted and under review. This page updates automatically when the result is in.',
    resubmit: 'The document needs to be photographed again. Please follow the prompts on the phone.',
    passed: 'Verification passed',
    passedHint: 'Identity verification for {name} is complete.',
    reverifyPassed: 'New ID verified',
    reverifyPassedHint: "{name}'s new ID has been verified. The update takes effect after our review.",
    reverifyRejected: "{name}'s new ID could not be verified. Please contact Client Services at {email}.",
    expired: 'This link is no longer valid. Select "New link" to get a fresh QR code.',
  },

  /** Verification link landing page (no sign-in) */
  kycInvite: {
    reverifyDesc: '{company} is updating your identity details and needs you to verify with your new ID. It takes about 3 minutes and no account registration is required.',
    reverifyWarn: 'Have your new ID document ready (not the old one) and make sure the lighting is good.',
    title: 'Complete your identity verification',
    desc: '{company} is opening a trading account and needs you to complete an identity verification. It takes about 3 minutes and no account registration is required.',
    role: 'Role',
    company: 'Company',
    warn: 'Have your identity document ready and make sure the lighting is good. Details that do not match the document send the verification to manual review.',
    review: 'Submitted and under review. You can close this page — the result goes to the company authorised representative.',
    resubmit: 'The document needs to be photographed again. Please follow the prompts on this page.',
    /* Full-card "under review" when the link is reopened — different from `review` above, which shows right after submitting */
    reviewTitle: 'Your details are under review',
    reviewDesc: 'You have already submitted a verification and we are checking it, so there is nothing more to submit for now. The result goes to the authorised representative of {company}.',
    doneTitle: 'Verification complete',
    doneDesc: 'Thank you. The result goes to the authorised representative of {company} — nothing further is needed from you.',
    endedTitle: 'This verification has ended',
    endedDesc: 'The verification behind this link has ended and cannot continue for now. Please contact the authorised representative of {company} about what happens next.',
    expiredTitle: 'This link is invalid or has expired',
    expiredDesc: 'Verification links are valid for 7 days. Please ask the company authorised representative who sent it to issue a new one.',
    contact: 'If you have questions, you can also contact Client Services:',
  },

  /** MT5 opening wait (V1.4 §5.5) */
  mt5Wait: {
    openingTitle: 'Your trading account is being opened',
    openingDesc: 'All your details have been verified and we are opening your MT5 trading account. We will email you as soon as it is ready.',
    openingBanner: 'Your trading account is being opened. We will email you as soon as it is ready.',
    banner: 'Your MT5 trading account could not be opened yet',
    failedDialogBody: 'You have completed every step of your application, but your trading account could not be opened automatically. We are aware of the issue. Please contact Client Services and we will complete the account opening for you.',
    openingDialogTitle: 'Your trading account is being opened',
    openingDialogBody: 'You have completed every step of your application. Opening usually takes a few minutes; once the account is ready we will email your MT5 login details. If you have not received them within 1 business day, please contact Client Services.',
  },

  /** Company information (V1.4 §6B) */
  company: {
    peopleHint: 'Each person below must complete their own Sumsub identity verification (ID document, liveness check and proof of address); the tag shows that person\'s result.',
    title: 'Company information',
    entityTitle: 'Company',
    legalName: 'Legal entity name',
    acn: 'ACN',
    incorporation: 'Date of incorporation',
    address: 'Registered office address',
    classification: 'Client classification',
    peopleTitle: 'Authorised representative and related persons',
    kycState: 'Verification: {state}',
    gotoPeople: 'Go to the verification list ›',
    noPeople: 'No related persons on record.',
    readOnlyNote: 'To change key details such as the legal entity name, ACN, directors, beneficial owners or the authorised representative, contact Client Services at {email} with the relevant ASIC documents. Application documents submitted at account opening are archived under our record-keeping policy; contact Client Services for a copy.',
  },

  pwd: {
    rowLabel: 'Login password',
    lastChanged: 'Last changed {date}',
    daysLeft: 'Expires in {n} days',
    expired: 'Expired — update it now',
    bannerText: 'Under our account policy, your Client Portal login password must be updated every 90 days. Yours expires in {n} days.',
    bannerAction: 'Change password',
    forceTitle: 'Update your password to continue',
    forceDesc: 'Under our account policy, your Client Portal login password must be updated every 90 days. Yours has expired — please set a new one to continue. (This is not your MT5 trading password.)',
    modalTitle: 'Your password expires soon',
  },

  overview: {
    accountIdOpening: '— (being assigned)',
    tagOpening: 'Opening',
    tagMt5Failed: 'Not opened yet',
    bannerOnboardCorp: '{n} more person(s) still to verify. We open the company trading account once everyone has passed.',
    corpTask: 'Complete identity verification',
    corpTaskDesc: 'The authorised representative, directors and ultimate beneficial owners each verify once.',
    corpTaskLink: 'View the verification list ›',
    corpTaskFoot: 'Funding, reports and platform downloads become available once everyone has passed and the MT5 account is open.',
    corpTaskCta: 'View the list',
    greeting: 'Hello, {name}',
    emailLine: 'Email: {email}',

    tasksTitle: 'Finish the last steps',
    task1: 'CFD knowledge test',
    task1Desc: '10 questions — 8 or more correct to pass',
    task2: 'Identity verification',
    task2Desc: 'Upload your ID and complete the face check; assessed automatically',
    task2Reviewing: 'Submitted. Our team is checking your details (usually within 1 business day); there is nothing more to submit.',
    taskResubmit: 'Action needed',
    task2Resubmit: 'Your ID photo was not accepted. Please retake and resubmit it; everything else you provided is saved.',
    startKycResubmit: 'Retake ID photo',
    bannerResubmit: 'Your ID photo was not accepted. Please retake and resubmit it',
    task2Locked: 'Pass the knowledge test first',
    taskExempt: 'Not required',
    task1Exempt: 'Corporate accounts skip the knowledge test; the authorised representative only completes identity verification',
    tasksDescCorporate: 'A corporate account needs no knowledge test — once every company person has completed identity verification, we open your live MT5 trading account automatically.',
    taskTodo: 'To do',
    taskDoing: 'In progress',
    taskReviewing: 'Under review',
    taskDone: 'Complete',
    startQuiz: 'Start knowledge test',
    startKyc: 'Continue verification',
    ctaFinish: 'Finish the last steps to open your account',
    tagProvisioning: 'Account being set up',
    tasksDescProvisioning: 'Both tasks are complete. We are opening your live MT5 trading account and will email you as soon as it is ready.',
    tasksDescOpenFailed: 'Unfortunately we were unable to open your trading account. Please contact support at {email} for details.',
    bannerOpenFailed: 'Unfortunately we were unable to open your trading account. Please contact support for details.',

    applyTitle: 'Finish your application first',
    applyDesc: 'This feature is available once your account is open. Your application has not been submitted yet — please continue from where you left off.',
    applyProgress: 'Application progress: {done}/5 steps complete · currently on step {step} · {name}',
    applyStep1: 'Submit your application',
    applyStep2: 'Complete the CFD knowledge test and identity verification',
    applyStep3: 'We open your live MT5 trading account automatically',
    continueApply: 'Continue application',
    bannerDraft: 'You have an unfinished application',
    bannerDraftDesc: '{done} of 5 steps complete, currently on "{step}. {name}". Everything you have entered is saved, so you can pick up where you left off.',
    tagDrafting: 'Application in progress',

    cfdAccount: 'CFD account',
    accountIdPending: '— (assigned once your account is open)',
    fundsLabel: 'Funds',
    marginLabel: 'Margin',
    availableLabel: 'Available',
    plLabel: 'P/L',
    balance: 'Balance',
    equity: 'Equity',
    floatingPl: 'Floating P/L',
    freeMargin: 'Free margin',
    marginLevel: 'Margin level',
    accountNote: 'Figures above are for trading account {login} ({type}).',
    leverageNote: 'Negative balance protection enabled',
    balanceOf: 'Balance ({cur})',
    tileDepositDesc: 'Bank transfer (AUD / USD)',
    tileWithdrawDesc: 'Transfer to your verified bank account',
    acctMeta: 'Account ID: {login} · Currency {currency}',
    accountDetails: 'Account details',
    colTime: 'Time',
    colType: 'Type',
    colMethod: 'Method',
    colCurrency: 'Currency',
    colAmount: 'Amount',
    colStatus: 'Status',

    bannerSuit1: 'You did not pass the suitability assessment this time. You can reapply after {date}; we will email you when that date arrives.',
    bannerKtCooling: 'Knowledge test not passed. You can retake it after {date} (we will email you).',
    bannerKtReady: 'The waiting period for your knowledge test has ended. You can retake it now.',
    bannerDup: 'Your application is under manual review and cannot proceed for now.',
    bannerTerminal: 'Unfortunately you do not currently meet our account opening criteria. Please contact us if you have questions.',
    bannerTerminalSuit2: 'Based on the Target Market Determination (TMD) assessment, we are unable to open an account for you. Please contact us if you have questions.',
    bannerTerminalAge: 'Unfortunately, your application was not successful.',
    viewDetails: 'Details',
    recentTitle: 'Recent activity',
    recentEmpty: 'No deposits or withdrawals yet.',
    recentViewAll: 'View all reports ›',
    typeDeposit: 'Deposit',
    typeWithdraw: 'Withdrawal',
    provisioningBanner: 'All your checks are complete. Your trading account is being set up and we will email you as soon as it is ready.',
    bannerOnboard: 'Finish opening your account',
    bannerMore: '＋ {n} more to handle',
    bannerCollapse: 'Collapse',

    quickDeposit: 'Deposit',
    quickWithdraw: 'Withdraw',

    staleData: 'Data as at {time}',
    fundsUnavailable: 'Balance information for this account is temporarily unavailable. Please refresh shortly.',
    noAccountYet: 'No account yet',
  },

  restricted: {
    suit1Title: 'Suitability assessment not passed yet',
    suit1Body: 'Based on your answers, you do not currently meet the Target Market Determination (TMD) for our CFD products. Your application has been paused; you may apply again after {date}, and we will email you a reminder then.',
    reapply: 'Apply again',
    reapplied: 'Your application has been reopened',
    // Non-terminal fallback: never tell a normal client they failed account opening
    unavailableTitle: 'This feature is currently unavailable',
    unavailableBody: 'Please try again later. If you have any questions, contact Client Services at {email}.',
    terminalTitle: 'Unfortunately you do not meet our account opening criteria',
    terminalBody: 'To find out more, or to close your account, please contact us at {email}.',
    suit2Title: 'We are unable to open an account for you',
    suit2Body: 'Unfortunately, based on the Target Market Determination (TMD) assessment, we are unable to open an account for you. This outcome is final and no further application is available. Please contact Client Services if you have questions, or to close your account.',
    ageTitle: 'Unfortunately, your application was not successful',
    ageBody: 'Unfortunately, your application does not currently meet our account opening requirements. If you believe this is a mistake or would like more information, please contact our Client Services team.',
    // 查重核实中（DUP，0.13.5 起可登录；D-96）：中性「审核中」，不是终态
    dupTitle: 'Your application is under review',
    dupBody: 'We have received your application and it is currently under manual review. The onboarding steps are paused while we review; your profile remains available to view. We will email you once the review is complete. For an update, contact Client Services at {email}.',
    kt1Title: 'Knowledge test on hold',
    kt1Body: 'Your knowledge test score was below the 80% pass mark. To give you time to review the material, you can retake it after 48 hours (from {date}). We will email you when the waiting period ends; you can then re-enter the test from “Finish the last steps”.',
    kt2Body: 'Your knowledge test score was below the 80% pass mark. To give you time to review the material, you can retake it after 6 months (from {date}). We will email you when the waiting period ends; you can then re-enter the test from “Finish the last steps”.',
    ktBlockedTitle: 'No knowledge test attempts left',
    readOnlyNote: 'Your account is read-only. Please contact us to change your details.',
  },

  quiz: {
    title: 'CFD knowledge test',
    exemptTitle: 'No knowledge test for corporate accounts',
    exemptBody: 'The knowledge test applies to individual clients only. Please complete identity verification as the authorised representative; we will then open your live MT5 trading account automatically.',
    ruleQuestions: '10 questions — 8 or more correct (80%) to pass',
    ruleCooldown: 'You have 3 attempts: after a first fail you must wait 48 hours, after a second fail 6 months, and a third fail ends the application.',
    attemptNotice: 'This is attempt {n} of {max}.',
    start: 'Start the test',
    resume: 'Resume your test',
    progress: 'Question {cur} of {total}',
    submit: 'Submit answers',
    submitConfirmTitle: 'Submit answers',
    submitConfirm: 'You cannot change your answers after submitting. Submit now?',
    unanswered: '{n} questions are still unanswered',
    passedScore: 'Passed ({score}/10)',
    passedNext: 'Next, complete identity verification and we will open your trading account.',
    continueKyc: 'Continue verification',
    failedTitle: 'You did not reach the pass mark',
    failedRetryAt: 'You can take the test again after {date}.',
    failedFinalTitle: 'Unfortunately you do not meet our account opening criteria',
    failedFinalBody: 'This was your third unsuccessful attempt, so we are unable to proceed with your application.',
    mailSent: 'We have emailed you the outcome and what happens next.',
    coolingUntil: 'Available again after {date}',
    noAttemptsLeft: 'No attempts left',
    alreadyPassed: 'You have passed the knowledge test',
    resumeHint: 'We have restored your previous answers.',
    answerFailed: 'That answer could not be saved. Please select it again.',
  },

  /** Re-verification progress card (pre-check / verification / invite pages; 2026-10-15). Mismatches are never disclosed to the client. */
  reverifyCard: {
    submittedTitle: 'New ID submitted and under review',
    submittedDesc: 'We are reviewing your new ID document. We will email you once the update to your details has been processed. You can leave this page.',
    submittedDescInvite: "Thank you for verifying with your new ID. We are reviewing it, and {company}'s authorised representative will be informed of the result. You can close this page.",
    passedTitle: 'New ID verified',
    passedDesc: 'The update to your details takes effect after our review, and we will email you then.',
    passedDescInvite: "The update takes effect after our review, and {company}'s authorised representative will be informed. You can close this page.",
    retryTitle: 'New ID could not be verified',
    retryDesc: 'Please check your ID and take new photos. If it still cannot be verified, contact Client Services at {email}.',
    rejectedTitle: 'New ID could not be verified',
    rejectedDesc: 'Please contact Client Services at {email} and we will help you.',
    submittedNow: 'Your new ID has been submitted and we are reviewing it. You can close this page.',
    rekycSubmittedTitle: 'Documents submitted, under review',
    rekycSubmittedDesc: "We are reviewing the documents you resubmitted at Client Services' request. You can leave this page.",
  },

  kyc: {
    reverifyIntro: 'We are updating your identity details as requested. Please verify your identity using your new ID document.',
    renewIntro: 'Your ID is about to expire or has expired. Please verify your identity again with a valid ID.',
    renewPassedTitle: 'ID updated',
    renewPassedDesc: 'You have verified with a valid ID, and its expiry date will be updated accordingly.',
    rekycIntro: 'Client Services has asked you to resubmit your verification documents. Please follow the prompts to complete identity verification.',
    rekycPassedTitle: 'Verification complete',
    rekycPassedDesc: "The documents you resubmitted at Client Services' request have been verified.",
    resubmitTitle: 'Please photograph your document again',
    resubmitBody: 'We received your details but need you to submit them again. Please retake the document photos — good lighting, all four corners visible, no glare.',
    title: 'Identity verification',
    intro: 'Have your ID document ready. It takes about 3 minutes and is captured and assessed by our verification provider.',
    startBtn: 'Start verification',
    mobileTitle: 'Continue on your phone',
    mobileHint: 'Open this page on your phone to use its camera. Progress syncs here in real time.',
    reviewing: 'Your documents are in and being assessed automatically (usually a few minutes)',
    reviewingHint: 'You can wait here or come back later — we will email you the outcome.',
    manual: 'Under manual review',
    manualHint: 'We are taking a closer look at your details and will email you the outcome.',
    passed: 'Identity verified',
    passedNext: 'We are opening your live MT5 trading account.',
    rejected: 'Unfortunately your verification was not successful',
    rejectedBody: 'To find out more, please contact us at {email}.',
    resubmit: 'Retake photos',
    channelDownTitle: 'Verification is being set up',
    channelDownBody: 'Our verification service is still being configured, so you cannot start just yet. We will email you as soon as it is ready — or come back and try again later.',
    quizFirst: 'Please pass the CFD knowledge test first',
    docExpiry: 'ID expiry',
  },

  onboardDone: {
    currencyNote: ' (as chosen in your application)',
    nbpValue: 'Enabled — your losses cannot exceed the funds in your account',
    riskWarn: 'CFDs are complex, high-risk leveraged products and you may lose money rapidly. Trade only if you fully understand the risks involved.',
    title: 'Your account is open',
    desc: 'Your live MT5 trading account is ready. You can make your first deposit and start trading.',
    login: 'MT5 account',
    server: 'Server',
    nbp: 'Negative balance protection enabled',
    mailSent: 'We have emailed you the details, including your MT5 login information.',
    firstDeposit: 'Make a deposit',
    provisioning: 'Setting up your account — we will email you when it is ready',
    provisioningHint: 'Everything has been approved and we are opening your trading account. We will email you as soon as it is done.',
    kycPassedTitle: 'Identity verified',
    kycPassedDesc: 'All your checks have been approved. Your trading account is being opened.',
    openFailedTitle: 'Account could not be opened',
    openFailedDesc: 'Unfortunately we were unable to open your trading account. Please contact support at {email} for details.',
  },

  mt5pwd: {
    resetTitle: 'Set a new MT5 trading password',
    resetDesc: 'This password is for logging in to the MT5 trading terminal. It is separate from your Client Portal password, and setting it will not sign you out of the portal.',
    newPassword: 'New trading password',
    ruleLength: '8–15 characters',
    // 裸 @ 会被 vue-i18n 当链接消息语法，整条消息编译失败——见 zh-CN 同键的说明
    ruleSymbol: 'at least 1 symbol (such as # {\x27@\x27} !)',
    ruleAscii: 'English letters, digits and keyboard symbols only, no spaces',
    done: 'Your new trading password is active',
    doneDesc: 'A confirmation email has been sent. Use the new password to log in to the MT5 terminal.',
    backToPortal: 'Back to Client Portal',
    linkExpiredDesc: 'Reset links are valid for 30 minutes and can only be used once. Sign in to the Client Portal and request a new one from My Accounts.',
    unknownTitle: 'Reset result not yet confirmed',
    unknownDesc: 'Your trading password may already have been updated. Please try logging in to the MT5 terminal with the new password first. If that does not work, request a new reset from My Accounts. To avoid confusion, this link can no longer be used.',
    goMyAccount: 'Go to My Accounts',
  },

  account: {
    accountName: 'Account name',
    corpTypeSuffix: ' (Wholesale client)',
    sophCorpTypeSuffix: ' (Corporate Sophisticated)',
    leverageByProduct: 'Varies by product category',
    leverageTable: 'View leverage table',
    pickAccount: 'Viewing trading account',
    detailsTitle: 'Account details',
    mt5Login: 'MT5 account',
    retailTypeSuffix: ' (Retail)',
    sophIndTypeSuffix: ' (Individual Sophisticated)',
    baseCurrency: 'Base currency',
    currency: 'Currency',
    nbp: 'Negative balance protection',
    nbpOn: 'Enabled',
    nbpNotApplicable: 'Not applicable',
    openedTime: 'Account opened',
    nbpNote: 'Negative balance protection: your losses cannot exceed your account balance.',  // 开户完成页 §6.4 仍在用
    title: 'My account',
    server: 'Server',
    type: 'Account type',
    leverage: 'Leverage',
    marginWarn: '{type} · {login}　Margin level {level}, at or below the {line} margin call level under our PDS',
    marginDanger: '{type} · {login}　Margin level {level}, at or below the {line} Stop Out Level — open positions may be closed automatically',
    depositNow: 'Deposit now',
    frozenNote: 'This account is frozen and trading is temporarily disabled: you cannot open or close positions, and deposits are not accepted. You can still withdraw and view reports.',
    disabledNote: 'This account is disabled. Please contact us to reinstate it.',
    closedNote: 'This account is closed.',
    changingNote: 'A change to this account is being processed. Please refresh shortly.',
    pendingNote: 'This account is being set up.',
    resetMt5Pwd: 'Reset MT5 password',
    resetMt5PwdTitle: 'Reset MT5 trading password',
    resetMt5PwdDesc: 'We will email a reset link to your registered address (valid for 30 minutes, single use). Set your new trading password from that email. This resets your MT5 trading password only. Your Client Portal password is unaffected and you will stay signed in.',
    resetMt5PwdSend: 'Send reset link',
    resetMt5PwdSent: 'A reset link has been sent to {email}. Please check your inbox.',
    resetMt5PwdUnavailable: 'The current account status does not allow a trading password reset.',
    // The demo account is read-only on this page (not part of the current account / funding / margin alerts, R-15)
    footerNote: 'Each account is denominated in its own currency, and funds are not shared between accounts. To open another trading account, please contact us.',
  },

  profile: {
    dob: 'Date of birth',
    amlScreening: 'AML screening',
    taxResidency: 'Tax residency',
    tinProvided: 'TIN provided',
    taxCountryUnknown: 'Country not provided',
    lastLogin: 'Last login',
    amlPassed: 'Passed',
    corpNote: 'This page shows your own verification and documents. Company details and the other related persons are here:',
    corpLinkCompany: 'Company information',
    corpLinkPeople: 'People and documents',
    title: 'My profile',
    keyInfoNote: 'To correct key details such as your name or date of birth, contact Client Services with supporting documents.',
    commTitle: 'Communication preferences',
    basicTitle: 'Personal details',
    openedTime: 'Opened',
    fullName: 'Name',
    email: 'Email',
    phone: 'Mobile',
    address: 'Residential address',
    notifyLocale: 'Notification language',
    notifyLocaleHint: 'Only affects the language of emails we send you. To change the interface language, use the switch at the top right.',
    notifyLocaleSaved: 'Notification language updated.',
    localeZh: '简体中文',
    localeZhHk: '繁體中文',
    localeEn: 'English',
    contactCsToChange: 'Contact us to change',
    contactCsHintEmail: 'Changing your email address requires us to verify your identity and approve the change. Please contact Client Services at {email}.',
    contactCsHintPhone: 'Changing your mobile number requires us to verify your identity and approve the change. Please contact Client Services at {email}.',
    contactCsHintAddress: 'Changing your residential address requires us to verify proof of your new address and approve the change. Please contact Client Services at {email}.',

    docTitle: 'Verification & documents',
    idProof: 'Proof of identity',
    addressProof: 'Proof of address',
    verified: 'Verified',
    notVerified: 'Not verified',
    docExpiryOn: 'ID expires {date}',
    reverify: 'Verify now',
    docPolicyNote: "From time to time, we'll ask you to update your documents so we can keep your account details current. This includes renewing your identification before it expires and providing a new proof of address every two years.",

    securityTitle: 'Security',
    closeAccountTitle: 'Closing your account',
    closeAccountNote: "If you'd like to close your account, please contact Client Services at {email}. Before we can complete the closure, all open positions must be closed, your account balance must be zero, and there must be no pending deposits or withdrawals.",
  },

  phoneFill: {
    title: 'Please add your mobile number',
    desc: 'To keep your account secure, please enter a mobile number you currently use to continue using the Client Portal.',
    numberLabel: 'Mobile number',
    codeLabel: 'Country code',
    invalidAu: 'Please enter a valid Australian phone number: 10 digits, starting with 02, 03, 04, 07 or 08.',
    invalid: 'Please enter a valid mobile number: 4–20 digits.',
    saved: 'Mobile number updated',
  },

  bank: {
    bankCountry: 'Bank country',
    bankCountryHint: 'For a non-Australian bank enter the two-letter country code (e.g. NZ); leave blank for Australian banks.',
    bankAddress: 'Bank address',
    bankAddressHint: 'Enter the full address of your bank branch (street, city, country). International transfers require it.',
    bankAddressRequired: 'Please enter the bank address',
    swiftHint: 'For international transfers, optional.',
    currencyHint: 'Must match the actual currency of the bank account; a mismatch will cause withdrawals to be returned by the bank.',
    presetTag: 'On file from account opening',
    presetNote: 'recorded at account opening',
    proposalNo: 'Request no.',
    submittedTime: 'Submitted',
    presetChange: 'Contact us to change',
    presetChangeTitle: 'Changing this bank account',
    presetChangeBody: 'This account was recorded with your application and forms part of the account opening file. Please contact Client Services to change it — we will verify the details and update it for you.',
    title: 'Bank accounts',
    add: 'Add a bank account',
    listEmpty: 'You have not added a bank account yet. Please click "+ Add a bank account" to start. Payments must come from a verified bank account in the name of the ZR Markets account holder. We cannot accept third-party payments.',
    bankName: 'Bank name',
    holderName: 'Account name',
    bsb: 'BSB',
    accountNo: 'Account number',
    accountTail: 'Last 4 digits',
    swift: 'SWIFT',
    currency: 'Account currency',
    statement: 'Bank statement',
    statementHint: 'Upload a bank statement from the last 12 months to confirm the account belongs to you.',
    statementRequired: 'Please upload your bank statement',
    pendingReview: 'Under review',
    approved: 'Approved',
    rejected: 'Declined',
    submitted: 'Submitted for review. Once approved you can use it for withdrawals.',
    holderMustMatch: 'The account holder must match your account name "{name}" exactly. Under our anti-money laundering policy we only accept accounts in your own name.',
    bsbInvalid: 'BSB must be 6 digits',
    accountNoInvalid: 'Please enter the account number',
    onePendingOnly: 'You already have a bank account under review. Please wait for the outcome before submitting another.',
    thirdPartyNote: 'Payments must come from a verified bank account in the name of the ZR Markets account holder. We cannot accept third-party payments.',
  },

  upload: {
    choose: 'Choose a file',
    uploading: 'Uploading…',
    remove: 'Remove',
    limits: 'PDF, JPG or PNG, up to 10MB per file.',
    tooLarge: 'That file is over 10MB. Please compress it and try again.',
    badType: 'Only PDF, JPG and PNG files are accepted.',
    notFound: 'That file no longer exists. Please upload it again.',
  },

  funding: {
    account: 'Trading account',
    proposalNo: 'Reference',
    viewRecords: 'View records',
    availableLabel: 'Available',
    noAccount: 'You do not currently have a trading account available for funding. Please contact support for assistance.',
    frozenMayReject: 'This account is frozen and trading is disabled. Your request may not be accepted; the outcome will be confirmed on submission.',
    noRate: 'No rate is currently available for this currency pair, so cross-currency requests cannot be submitted at this time.',
    voucherInvalid: 'The remittance receipt is invalid or has expired. Please upload it again.',
  },

  deposit: {
    addBank: '+ Add a bank account',
    title: 'Deposit',
    remarkHint: 'Please quote your MT5 account number ({login}) as the transfer reference so we can match your payment quickly.',
    currency: 'Deposit currency',
    amount: 'Deposit amount',
    amountPh: 'Enter the deposit amount',
    remitDate: 'Transfer date',
    remitDateHint: 'Optional. Providing it helps us match your payment sooner.',
    payer: 'Paying bank account',
    payerHint: 'You may only pay from a verified bank account held in your own name.',
    noPayer: 'You do not have a verified bank account yet. Please add one under Bank Accounts first.',
    voucher: 'Remittance receipt',
    voucherHint: 'Upload the bank receipt or transfer confirmation for this payment.',
    expected: 'Estimated credit',
    fxLead: 'The selected account ({login}) is denominated in {cur}. A {from} deposit will be converted at the live rate on arrival.',
    liveRate: 'Live rate 1 {from} = {rate} {to}',
    termsTitle: 'Before you deposit',
    terms1: 'Payments must come from a verified bank account in the name of the ZR Markets account holder. We cannot accept third-party payments.',
    terms2: "We'll credit your trading account once your payment has been received and reconciled, and we'll keep you updated by email.",
    terms3: 'By submitting a deposit notice, you confirm that the funds are from a legitimate source and that you have read the PDS, FSG and Client Agreement.',
    terms4: 'Deposits made in a different currency will be converted at the exchange rate applied when your deposit is processed. This may differ from the indicative rate shown.',
    receiving: 'Company receiving account',
    receivingNote: 'Transfer from your own bank account to the account below, then submit this form.',
    noReceiving: 'No receiving account is available for this currency. Please contact support.',
    bankTransfer: 'Bank transfer',
    accountName: 'Account name',
    bankName: 'Bank name',
    bankAddress: 'Bank address',
    accountNo: 'Account number',
    submit: 'Submit deposit notice',
    submitNote: 'We will credit your account once the funds are received and verified. You will be notified by email.',
    submitted: 'Deposit notice submitted',
    submittedNote: 'We will credit your account once the payment is verified. You can track progress under Deposit Records.',
    another: 'Submit another',
  },

  withdraw: {
    title: 'Withdrawal',
    stepAccount: 'Withdraw from trading account',
    stepAmount: 'Withdrawal amount ({cur})',
    stepBank: 'Receiving bank account',
    accountCurrency: 'Account currency',
    withdrawableLabel: 'Withdrawable',
    amount: 'Withdrawal amount',
    fillMax: 'Withdraw all',
    overLimit: 'The withdrawal amount cannot exceed the withdrawable amount of account {login} ({amount} {cur}).',
    availNote: 'The withdrawable amount is calculated by the system from this account\x27s balance, equity and margin in use.',
    unsettledNote: 'A further {amount} {cur} of recent deposits is still clearing and cannot be withdrawn yet.',
    noBankTitle: 'Add a bank account first',
    noBankBody: 'Withdrawals are paid to an approved bank account in your own name. Go to Funding → Bank Accounts to add one and upload a statement.',
    goBank: 'Add a bank account',
    bankPendingTitle: 'Bank account under review',
    bankPendingBody: 'Your bank account is being reviewed. You can withdraw once it is approved (we will email you the result).',
    holder: 'Account name',
    manageBanks: 'Manage',
    creditCurrency: 'Credit currency (bank account currency)',
    creditFollows: 'follows the selected bank account',
    noSameCurrencyBank: 'You have no approved {cur} bank account — the amount will be converted at the reference rate.',
    refRate: 'Reference rate',
    expected: 'Estimated credit',
    expectedIn: 'Estimated credit ({cur})',
    transferNote: 'Funds are sent only to a bank account in your own name — usually up to 5 business days.',
    ack: 'I understand that the amount is deducted from my MT5 account on submission and cannot be cancelled through the Client Portal. If the request is not approved, the funds will be returned to my trading account.',
    submit: 'Submit withdrawal request',
    frozenNote: 'Account {login} is currently frozen and trading is temporarily disabled: deposits are not accepted, but you can still withdraw funds from this account.',
    currencySwitched: 'Amount currency switched to {cur}',
    expectedEstimated: 'Estimated credit (at the reference rate when submitted)',
    expectedReference: 'Indicative credit (final amount is based on the rate applied at approval)',
    accountLine: '{login} ({type})',
    submitted: 'Withdrawal request submitted',
    another: 'Submit another',
    unknownTitle: 'Confirming your request',
    unknownNote: 'The outcome of this submission has not yet been confirmed. We are verifying the deduction status on your account.',
    unknownNoRetry: 'Please do not submit this request again. Check Withdrawal Records shortly for the outcome, and contact support if you have questions.',
  },

  records: {
    depositSettled: 'Deposit records',
    depositApplications: 'Deposit requests',
    withdrawalSettled: 'Withdrawal records',
    withdrawalApplications: 'Withdrawal requests',
    allAccounts: 'All accounts',
    mt5TimeNote: 'All times below are MT5 server time (GMT+3); amounts are in the currency of the trading account each record belongs to.',
    applyTime: 'Submitted',
    settledTime: 'Settled',
    rate: 'Rate',
    voucher: 'Receipt',
    export: 'Export',
    viewVoucher: 'View',
    voucherOpenFailed: 'The receipt could not be opened right now. Please try again later.',
    rangeAll: 'All time',
    range7d: 'Last 7 days',
    range30d: 'Last 30 days',
    range90d: 'Last 90 days',
    rangeYtd: 'This year',
    proposalNo: 'Proposal no.',
    account: 'Trading account',
    channel: 'Channel',
    amount: 'Amount',
    settledAmount: 'Settled amount (account currency)',
    expectedAmount: 'Expected credit (account currency)',
    dealIdNote: 'The MT5 Deal ID is the balance deal number of this transfer in your MT5 account, for reconciliation with the platform.',
    dealId: 'MT5 Deal ID',
    status: 'Status',
    empty: 'No records yet.',
    settledEmptyHint: 'Only completed records appear here. Requests still in progress are under',
    action: 'Action',
    reinitiate: 'Submit again',
    statusNotesTitle: 'Status notes',
    statusHint: {
      deposit: {
        PROCESSING: 'Processing: we have received your deposit notice and are confirming receipt of the funds.',
        SETTLED: 'Settled: the funds have been credited to your MT5 trading account.',
        CANCELLED: 'Cancelled: this deposit notice was not completed. If you have already transferred the funds, please submit again with your remittance receipt.',
      },
      withdrawal: {
        REVIEWING: 'Under review: the amount has been deducted from your MT5 trading account and is being reviewed.',
        COMPLETED: 'Completed: the funds have been paid to your nominated bank account.',
        REFUNDED: 'Returned: this request was not approved and the amount has been returned to your MT5 trading account. You may submit again.',
        REJECTED: 'Unsuccessful: this request was cancelled or expired. The deducted amount will be returned to your MT5 trading account, and you may submit again.',
      },
    },
    pageInfo: 'Page {page} of {max} · {total} records',
    prev: 'Previous',
    next: 'Next',
  },

  platform: {
    corpHolderNote: 'The account holder is {company}. The authorised representative trades using the MT5 login and trading password.',
    title: 'Platform download',
    liveTag: 'Live',
    demoTag: 'Demo',
    liveTitle: 'Live account',
    demoTitle: 'Demo account',
    loginAccount: 'Login',
    server: 'Server',
    password: 'Password',
    passwordValue: 'The MT5 trading password sent to you in your account-opening email (different from your Client Portal login password)',
    noAccountYet: 'Your trading account is still being opened. The account number and currency will appear here once it is ready. You can download and install the platform now — use the server name below.',
    downloadOnlyNote: 'You can download the MetaTrader 5 platform here. Your login details and server will appear on this page once a trading account is open.',
    demoPasswordNote: 'Your demo account password was emailed to you when it was created.',
    serverWarn: 'Live and demo accounts sit on different servers — choose the server that matches your login.',
    notSameAsPortal: 'Your MT5 trading password is not your Client Portal password. If you forget it, reset it under Accounts → My Account → Reset MT5 password.',
    multiAccountNote: 'Showing trading account {login} ({type}). You have {n} live trading accounts — switch accounts in the top bar to see the matching login.',
    downloadTitle: 'Download MetaTrader 5',
    windows: 'Windows',
    macos: 'macOS',
    ios: 'iOS',
    android: 'Android',
    demoNotOpened: 'No demo account yet',
  },

  support: {
    title: 'Help & support',
    desc: 'The button below opens the email app on your device with a message to our Client Services team.',
    sendMail: 'Email Client Services',
    subject: 'Client enquiry — ZR Client Portal',
    fallback: 'If nothing happens when you click, your device has no default email app set up. Please email us directly at:',
  },

  dict: {
    corpPersonRole: {
      AUTH_REP: 'Authorised representative',
      DIRECTOR: 'Director',
      UBO: 'Ultimate beneficial owner',
    },
    corpPersonStatus: {
      TODO: 'Not started',
      SENT: 'Link sent',
      REVIEW: 'Under review',
      PASSED: 'Verified',
      RESUBMIT: 'Resubmission needed',
      FAILED: 'Not verified',
      REMOVED: 'Removed',
    },
    docState: {
      VALID: 'Valid',
      EXPIRING: 'Expiring soon',
      EXPIRED: 'Expired',
      REVIEW: 'Under review',
      // v1 migration left no expiry date on file — to be back-filled, NOT expired
      NOT_COLLECTED: 'Expiry date missing',
    },
    idType: {
      PASSPORT: 'Passport',
      DRIVER_LICENCE: 'Driver licence',
      NATIONAL_ID: 'National ID',
    },
    clientClassification: {
      WHOLESALE_S761G: 'Wholesale client (s761G)',
      SOPHISTICATED: 'Corporate Sophisticated client',
    },
    mt5Status: {
      PENDING: 'Being set up',
      ENABLED: 'Active',
      FROZEN: 'Frozen · trading disabled',
      DISABLED: 'Disabled',
      CLOSED: 'Closed',
      CHANGING: 'Updating',
    },
    channel: {
      BANK_TRANSFER: 'Bank transfer',
      PAYID: 'PayID',
    },
    bankStatus: {
      PENDING_REVIEW: 'Under review',
      APPROVED: 'Approved',
      REJECTED: 'Declined',
      REPLACED: 'Replaced',
    },
    facing: {
      IN_PROGRESS: 'Account opening in progress',
      DECLINED: 'Application unsuccessful',
      DONE: '',
    },
  },

  footer: {
    entity: 'Zircon Financial Service (Australia) Pty Ltd',
    acn: 'ACN 672 642 054',
    afsl: 'AFSL 562700',
    tradingAs: 'trading as ZR Markets',
    risk: 'Trading in CFDs carries a high level of risk and may not be suitable for all investors. You may lose more than your initial investment. Please read our {pds}, {fsg} and {tmd} before deciding to trade.',
  },
}

export default messages
